(ns dashboard.components.file-upload.view
  (:require [reagent.core :as r]
            [re-frame.core :as rf]
            [clojure.string :as str]
            [dashboard.ui :as ui]
            [dashboard.components.file-upload.subs :as file-subs]
            [dashboard.components.file-upload.events :as file-events]))

(defn remove-button [on-remove]
  [:button.button.is-small.is-text.is-rounded
   {:type     "button"
    :on-click on-remove}
   "remove"])

(defn file-item [file-name upload-status on-remove]
  [:span.panel-block {:style {:min-height 30}}
   [:div.is-fullwidth.level.is-flex-grow-1.space-between
    [:p.level-left file-name]
    [:div.level-right
     (case upload-status
       :completed [:p.mr-3.has-text-success.is-size-5 "✔"]
       :uploading [:button.button.is-small.is-text.is-loading.is-rounded]
       :failed [:p.mr-3.has-text-danger.is-size-5 "Failed"]
       :already-exists [:div.columns.is-vcentered.mr-auto
                        [:p.mr-3.has-text-danger.is-size-5 "Duplicate"]
                        [remove-button #(on-remove file-name)]]
       [remove-button #(on-remove file-name)])]]])

(defn file-list [files existing-files all-uploaded on-remove]
  [:ul
   (for [[file-name {:keys [upload-status]}] files]
     (let [exists (contains? existing-files file-name)]
       ^{:key file-name}
       [file-item file-name (if (and (not all-uploaded) exists) :already-exists upload-status) on-remove]))])

(defn drag->change
  "Converts a drop event into a change event that can be fed to `on-change` in `file-picker`."
  [event accepted]
  (let [items (.. event -dataTransfer -items)
        files (reduce (fn [files item]
                        (if-let [file (and (= (.-kind item) "file")
                                           (.getAsFile item))]
                          (if (some #(str/ends-with? (.-name file) %) accepted)
                            (conj files file)
                            files)
                          files))
                      [] items)]
    (clj->js {:target {:files files}})))

(defn file-picker []
  (let [is-hovering (r/atom false)]
    (fn [props]
      (let [{:keys [name
                    selected
                    uploaded
                    on-change
                    on-remove
                    upload-status
                    accept
                    all-uploaded]} props
            is-uploading?          (= upload-status :uploading)
            is-upload-completed?   (= upload-status :completed)
            existing-files         (set (map :filename uploaded))
            hovering?              @is-hovering]
        [:div.column {:class         (when hovering? "has-background-info-light")
                      :on-drag-enter (fn [e] (.preventDefault e))
                      :on-drag-over  (fn [e]
                                       (.preventDefault e)
                                       (reset! is-hovering true))
                      :on-drag-leave #(reset! is-hovering false)
                      :on-drop       (fn [e]
                                       (.preventDefault e)
                                       (reset! is-hovering false)
                                       (on-change (drag->change e (str/split accept ", "))))}

         [:div.level
          [:div.level-left]
          (when (every? false? [is-uploading? is-upload-completed?])
            [:div.level-right.file
             [:label.file-label.level-item
              [:input.file-input {:multiple  true
                                  :name      name
                                  :on-change on-change
                                  :type      "file"
                                  :accept    accept}]
              [:span.button.is-rounded.is-small "Browse Files"]]])]

         ;; Render list of selected files
         [:div.column {:style {:overflow "scroll" :min-height "20vh" :max-height "70vh"}
                       :class (when (empty? selected) "is-justify-content-center")}
          (when (empty? selected)
            [:p.mx-auto (if (= upload-status :completed)
                          "The file(s) have been successfully uploaded."
                          "You can either or drag and drop files here or use the “Select files” button above!")])
          [file-list selected existing-files all-uploaded on-remove]]]))))

(defn upload-modal
  [{:keys [title file-type uploaded-files accepted-file-formats context success-message]}]
  (let [upload-status        @(rf/subscribe [::file-subs/upload-status file-type])
        selected-files       @(rf/subscribe [::file-subs/selected-files file-type])
        is-uploading?        (= upload-status :uploading)
        is-upload-completed? (= upload-status :completed)
        disable-buttons?     (or is-uploading? (= 0 (count selected-files)))
        on-file-select       #(rf/dispatch [::file-events/select-files file-type %])
        on-remove            #(rf/dispatch [::file-events/remove-file file-type %])
        on-clear             #(rf/dispatch [::file-events/clear-files file-type])
        on-upload            #(rf/dispatch [::file-events/initiate-file-upload-process uploaded-files file-type context])
        on-clear-state       #(rf/dispatch [::file-events/reset file-type])
        upload-statuses      (set (map :upload-status (vals selected-files)))]
    [:div.card
     [:div.card-header.has-background-secondary.is-shadowless
      [:title.card-header-title title]
      [:button.card-header-icon {:on-click #(rf/dispatch [::ui/pop-modal])}
       [:span.icon [:i.fas.fa-xmark]]]]
     [:div.card-content.p-0
      [:form [file-picker {:selected       selected-files
                           :uploaded       uploaded-files
                           :on-change      on-file-select
                           :on-remove      on-remove
                           :on-upload      on-upload
                           :on-clear       on-clear
                           :upload-status  upload-status
                           :on-clear-state on-clear-state
                           :accept         accepted-file-formats
                           :all-uploaded   (= #{:completed} upload-statuses)}]]]
     (when (and success-message is-upload-completed?)
       [:div success-message])
     [:div.card-footer
      (if is-upload-completed?
        [:button.button.is-inverted.is-success.card-footer-item
         {:type     "button"
          :on-click #(rf/dispatch [::file-events/reset file-type])}
         "Upload More Files"]
        [:<>
         [:button.button.is-inverted.is-info.card-footer-item
          {:disabled (empty? selected-files)
           :on-click #(rf/dispatch [::file-events/clear-files file-type])} "Clear"]
         [:button.button.is-inverted.is-info.card-footer-item
          {:class    (when is-uploading? "is-loading")
           :disabled disable-buttons?
           :on-click #(rf/dispatch [::file-events/initiate-file-upload-process uploaded-files file-type context])} "Upload"]])]]))
